import {useEffect} from 'react';

import {ProcedureValidationError} from 'procedures/design/validation/ProcedureValidationTypes';
import {findNewValidationErrors} from 'procedures/design/validation/procedureValidationUtils';
import {validateProcedure} from 'procedures/design/validation/validateProcedure';
import {trackEncounteredConfigurationValidationError} from 'procedures/procedureAnalyticsEventsUtil';
import {ProcedureFlowchart, ProcedureMetadata} from 'procedures/ProcedureDomainTypes';
import {
  useAllTagSchemasWithProceduresShape,
  useAllTagSchemasWithStandardShape,
} from 'procedures/utils/useAllTagSchemas';
import useBioentitiesForProcedures, {
  getBioentityIdsFromFlowchart,
} from 'procedures/utils/useBioentitiesForProcedures';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';

/**
 * Other than loading schemas, this hook has no side effects. Callers are require to cache
 * or otherwise respond to validation errors if needed.
 */
export default function useValidateProcedure(
  flowchart: ProcedureFlowchart,
  procedureMetadata: ProcedureMetadata,
  procedureId: string
): Array<ProcedureValidationError> {
  const tagSchemasForProcedures = useAllTagSchemasWithProceduresShape();
  const tagSchemas = useAllTagSchemasWithStandardShape();
  const bioentitiesForProcedures = useBioentitiesForProcedures(getBioentityIdsFromFlowchart(flowchart));

  const [procedureValidationErrors, setProcedureValidationErrors] = useStateIfMounted<
    Array<ProcedureValidationError>
  >(
    validateProcedure(flowchart, procedureMetadata, {
      tagSchemas,
      tagSchemasForProcedures,
      bioentitiesForProcedures,
    })
  );

  useEffect(() => {
    const previousValidationErrors = procedureValidationErrors;

    const currentValidationErrors = validateProcedure(flowchart, procedureMetadata, {
      tagSchemas,
      tagSchemasForProcedures,
      bioentitiesForProcedures,
    });
    const newValidationErrors = findNewValidationErrors(previousValidationErrors, currentValidationErrors);
    trackEncounteredConfigurationValidationError(
      procedureId,
      procedureMetadata.procedureType,
      newValidationErrors
    );
    setProcedureValidationErrors(currentValidationErrors);
    // Do not include procedureValidationErrors as a dependency to prevent infinite re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowchart, procedureMetadata, tagSchemas]);

  return procedureValidationErrors;
}
